/*
Documentation and examples for the component
https://react-pdf.org/components
https://blog.usejournal.com/lets-make-a-resume-in-react-2c9c5540f51a
*/

import React from 'react';
import PropTypes from 'prop-types';
import {
  Page, Text, View, Document, Font, Image, pdf,
} from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import {
  humanizeDaysDisplay,
  getTimePeriod,
  transform28HoursTo24Hours,
  sort28HoursFormat,
} from '../../../utils/Humanizers';
import { getRouteLabel, getStopDescription, searchModeRouteTypeMap } from '../../../services/TimetablesService';
import YellowIconBusPng from '../../../assets/png/yellowicon-bus-3x.png';
import { ReactComponent as IconDownload } from '../../../assets/svg/download.svg';
import YellowIconFerryPng from '../../../assets/png/yellowicon-ferry-3x.png';
import YellowIconTrainPng from '../../../assets/png/yellowicon-train-3x.png';
import ATLogoPng from '../../../assets/png/at-logo-150.png';
import AppleBadgePng from '../../../assets/png/app_badge_apple.png';
import GoogleBadgePng from '../../../assets/png/app_badge_google.png';
import styles from './DownloadTimetableLink_Style';
import OpenSansRegular from '../../../assets/fonts/opensans.woff';
import OpenSansBold from '../../../assets/fonts/opensansbold.woff';

const openSansBoldUrl = process.env.REACT_APP_WAYFINDING_CDN_URL ? `${process.env.REACT_APP_WAYFINDING_CDN_URL}/fonts/opensansbold.woff` : OpenSansBold;
const openSansRegularUrl = process.env.REACT_APP_WAYFINDING_CDN_URL ? `${process.env.REACT_APP_WAYFINDING_CDN_URL}/fonts/opensans.woff` : OpenSansRegular;
const bem = 'tt-download-link';

// Using webpack so that its available in local test. In actual test, setup .env CDN_URL to point to the font
// typically its http://az608295.vo.msecnd.net/public/fonts/opensans.woff andd http://az608295.vo.msecnd.net/public/fonts/opensansbold.woff
Font.register({ family: 'OpenSansBold', src: openSansBoldUrl });
Font.register({ family: 'OpenSansRegular', src: openSansRegularUrl });

// Create Document Component, this basically puts list of time into 2 buckets, AM/PM
// This function is used by a reducer to segregate a list of time into AM PM buckets. It is called using js array.reduce
// Example Input: ['1:00:00', '14:00:00']
// Example Output: { AM: ['1.00'], PM: [ '2.00']}
export const reducerAmPm = (result, current, index, ary) => {
  const newresult = { ...result };
  const visitedPM = newresult.visitedPM;
  const key = getTimePeriod(current);
  const formattedTime = transform28HoursTo24Hours(current);
  const time = `${formattedTime.substring(0, 2)}.${formattedTime.substring(3, 5)}`;
  const nkey = visitedPM ? 'PM' : key; // if PM is seen before, all subsequent AM will be put in the PM bucket (because its the night bus after the PM slot)

  if (nkey === 'PM') newresult.visitedPM = true;
  newresult[nkey] = newresult[nkey] || [];
  newresult[nkey].push(time);

  if (ary.length === (index + 1)) delete newresult.visitedPM; // clear the state variable when processing is done
  return newresult;
};

// Background
// This function is use to compute the height of the AM or PM (left/right) container. The container uses flex and flex-wrapping
// is enabled. We also need to use pdfkit wrap=false property to prevent section from rendering inbetween 2 pages. (unless content is more than a page)
//
// 1. Given a list of AM PM already in buckets
// 2. Compute optimum height for both AM and PM
// 3. Take the max of both (because both are displayed together)
//
// The optimum height is base on the following method:
// Observing from the printout, we can see that a maximum of 5 X 24 can fit in a 178mm heigth content
// Therefore,
// 1. Given the number of items to show (ie. time slots)
// 2. Base on the number, we map to a page size
// 0 - 30 items is 1/4 page (ie. 178mm * 0.25 wrap=false)
// 31 - 60 items is 1/2 page (ie. 178mm * 0.5 wrap=false)
// 61 - 90 items is 3/4 page (ie. 178mm * 0.75 wrap=false)
// 91 - 120 items is full page (ie. 178mm wrap=false)
// >120 means it cannot fit into a single page, therefore we need to turn the PDF wrap property to true (to make it flow to the next page)
// The wrap property is used so that each section of AM/PM will not break halfway inbetween pages (this improves the look fo the printed schedule)
export function computeOptimizedLayout(amPmSet) {
  const PAGE_HEIGHT_MM = 185; // mm
  const NORMAL_FONT_SIZE = 11; // pt
  const NORMAL_MARGIN_RIGHT = 8; // mm

  // Using simple mapping of number of time slots to heigh (either 1/2 page or full page)
  const layout = ['AM', 'PM'].filter(ampm => amPmSet[ampm]) // removing empty AM or PM
    .map((ampm) => {
    // Computing each AM or PM, the height and wrap
      const len = amPmSet[ampm].length;
      let wrap = false;
      let fontSize = NORMAL_FONT_SIZE;
      let marginRight = NORMAL_MARGIN_RIGHT;
      let height = PAGE_HEIGHT_MM;
      if (len <= 30) height *= 0.25;
      else if (len <= 60) height *= 0.5;
      else if (len <= 90) height *= 0.75;
      else if (len > 120) {
      // NOTE: Found out that CTY - stop 1061 - Jellicoe have a lot of timeslots, and overflow to next page does not work properly.
      // Therefore, added fontSize and marginRight to adjust so that this stop can fit in a page
        wrap = true; // Content more than a page, so we need to allow page wrapping
        fontSize = 9;
        marginRight = 5;
      }
      return {
        ampm, height, wrap, fontSize, marginRight,
      };
    }).reduce((accum, item) => {
    // Computing the max of both AM/PM and if wrapping should be enabled
      let newaccum;

      if (accum == null) {
        newaccum = { ...item };
        newaccum.height = item.height.toString();
        newaccum.fontSize = item.fontSize.toString();
        newaccum.marginRight = item.marginRight.toString();
      } else {
        newaccum = { ...accum };
        newaccum.height = `${Math.max(accum.height, item.height)}mm`;
        newaccum.fontSize = `${Math.min(accum.fontSize, item.fontSize)}pt`;
        newaccum.marginRight = `${Math.min(accum.marginRight, item.marginRight)}mm`;
        if (!accum.wrap && item.wrap) newaccum.wrap = item.wrap;
      }
      return newaccum;
    }, null);

  return layout;
}

// ------------- DUMB Components
// Used to display AM part or PM part
// We adjust height, fontSize marginRight base on the number of time slots. If there more timeslots, we need to reduce
// font size and spacing (margin-right)
export const ScheduleSection = ({
  amPmSet, height, fontSize, marginRight,
}) => (
  <View style={styles.schedule}>
    {
        ['AM', 'PM']
          .filter(period => !!amPmSet[period])
          .map(period => (

            <View style={styles.schedule_ampm} key={period}>
              <Text style={styles.schedule_ampm.heading_ampm}>{period}</Text>
              <View style={[styles.schedule_ampm.container_ampm, { height }]}>
                {amPmSet[period].map((time, index) => {
                  const key = index;
                  return (
                    <Text
                      key={key}
                      style={[styles.schedule_ampm.timetext, {
                        fontSize, marginRight,
                      }]}
                    >
                      {time}
                    </Text>
                  );
                })}
              </View>
            </View>

          ))
    }
  </View>
);
ScheduleSection.propTypes = {
  amPmSet: PropTypes.object.isRequired,
  height: PropTypes.string.isRequired,
  fontSize: PropTypes.string.isRequired,
  marginRight: PropTypes.string.isRequired,
};

// The yellow icon used in header. Extracted out for easier unit test validation because PDF component need to be
// mocked
export const yellowIcon = ({ route_type: routeType }) => {
  switch (routeType) {
    case searchModeRouteTypeMap.FERRY: return YellowIconFerryPng;
    case searchModeRouteTypeMap.TRAIN: return YellowIconTrainPng;
    case searchModeRouteTypeMap.BUS: return YellowIconBusPng;
    default:
      throw new Error(`routeType not found:${routeType}`);
  }
};

// the header itself
export const PDFHeader = ({ selectedHeadsign, selectedStop }) => {
  const routeLabel = getRouteLabel(selectedHeadsign);
  const stopLabel = getStopDescription(selectedStop);
  const { header: headerStyle } = styles;
  return (
    <View fixed style={headerStyle}>
      <Image fixed style={headerStyle.transport_icon} src={yellowIcon(selectedHeadsign)} />
      <View fixed style={headerStyle.text_section}>
        <Text fixed style={headerStyle.routetext}>{routeLabel}</Text>
        <Text fixed style={headerStyle.stoptext}>{stopLabel}</Text>
      </View>
      <Image fixed style={headerStyle.at_logo} src={ATLogoPng} />
    </View>
  );
};
PDFHeader.propTypes = {
  selectedHeadsign: PropTypes.object.isRequired,
  selectedStop: PropTypes.object.isRequired,
};

// Footer
export const PDFFooter = () => {
  const { footer: footerStyle } = styles;
  return (
    <View fixed style={footerStyle}>
      <View fixed style={footerStyle.line} />
      <View fixed style={footerStyle.text_section}>
        <Text fixed style={footerStyle.title}>Download the AT Mobile APP</Text>
        <Text fixed style={footerStyle.content}>AT Mobile is the latest APP from Auckland Transport to help you find your way around the city&apos;s public transport network</Text>
      </View>
      <Image fixed style={footerStyle.apple_badge} src={AppleBadgePng} />
      <Image fixed style={footerStyle.google_badge} src={GoogleBadgePng} />
    </View>
  );
};


// This is the day section
// Weekdays
// AM    PM
export const DaySection = (props) => {
  const { timetables } = props;
  const docprops = {
    title: 'Timetable',
    creator: 'AT Website',
    producer: 'AT Website',

  };
  return (
    <Document {...docprops}>
      <Page size="A4" style={styles.page}>
        <PDFHeader {...props} />
        {timetables.map((timetable) => {
          const { days, schedule } = timetable;
          const amPmSet = [...schedule].sort(sort28HoursFormat).reduce(reducerAmPm, {});
          const layout = computeOptimizedLayout(amPmSet);
          return (
            <View style={styles.day_section} key={days.join(':')} wrap={layout.wrap}>
              <Text style={styles.day_section.heading}>{ humanizeDaysDisplay(days)}</Text>
              <ScheduleSection amPmSet={amPmSet} height={layout.height} fontSize={layout.fontSize} marginRight={layout.marginRight} />
            </View>
          );
        })}
        <PDFFooter />
      </Page>
    </Document>
  );
};


// Filename geneartion
const generateFilename = ({ routeShortName, selectedStop, selectedRoute }) => {
  const { trip_headsign: routeLongName } = selectedRoute;
  const { stop_code: stopCode } = selectedStop;

  const filename = [
    routeShortName.trim(),
    'ST',
    stopCode.trim(),
    routeLongName.trim().replace(/ /g, ''),
  ].join('-');
  return `${filename}.pdf`;
};

export const GeneratedLink = ({ loading }) => {
  if (loading) return <p>Generating Link...</p>;
  return (
    <div className={bem}>
      <IconDownload className={`${bem}__icon`} role="none" />
      <p className={`${bem}__text`}>Download stop timetable</p>
    </div>

  );
};
GeneratedLink.propTypes = {
  loading: PropTypes.bool.isRequired,
};

const downloadPdf = async (props) => {
  const doc = <DaySection {...props} />;
  const blob = await pdf(doc).toBlob();
  saveAs(blob, generateFilename(props));
};

const DownloadTimetableLink = props => (
  <button
    type="button"
    className={`${bem}__button at-btn at-btn--borderless`}
    onClick={async () => downloadPdf(props)}
  >
    <div className={bem}>
      <IconDownload className={`${bem}__icon`} role="none" />
      <p className={`${bem}__text`}>Download stop timetable</p>
    </div>
  </button>
);

export default DownloadTimetableLink;